import React, { useMemo } from "react";
import { Link } from "@iventis/utilities";
import { format } from "date-fns";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Body2, PrimaryTextLink, editStyleFormGap, narrowPadding, skeletonShimmer, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Node } from "@iventis/domain-model/model/node";
import { Content } from "@iventis/translations";
import { findNode, findParentNodeByType } from "@iventis/tree-browser";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { useQuery } from "@tanstack/react-query";
import { getTree } from "../services/library-api-functions";
import { TreeVisualizer } from "./tree-visualizer";
import { createHrefToNode } from "../plan-library/mapping-library.helpers";

interface LayerInformationProps {
    node: Node;
    openPermissionsModal: () => void;
}

export const LayerInformation: React.FunctionComponent<LayerInformationProps> = ({ node, openPermissionsModal }) => {
    const translate = useIventisTranslate();

    const { data: tree } = useQuery(["layer-info-tree", node.sourceId], () => getTree(node.sourceId));

    // Generate the href to take the user to the folder that the map is in that the source of this node resides
    const layerLocationHref = useMemo(() => {
        if (tree == null || node == null) {
            return <SkeletonPlaceholder>{translate(Content.map4.layer_information.open_in_folder)}</SkeletonPlaceholder>;
        }
        const sourceNode = findNode([tree], node.sourceId);
        const parentNode = findParentNodeByType(tree, NodeType.Map, sourceNode);

        return (
            <StyledLink data-testid="layer-info-open-in-folder" to={createHrefToNode(tree.id, parentNode.parentId)} onClick={(e) => e.stopPropagation()}>
                <Body2>{translate(Content.map4.layer_information.open_in_folder)}</Body2>
            </StyledLink>
        );
    }, [node, tree, translate]);

    const parentNode = useMemo(() => findNode([tree], findNode([tree], node.sourceId)?.parentId), [tree, node]);

    return (
        <StyledContainer>
            <div>
                <Body2>{translate(Content.map4.layer_information.owner_map_location)}</Body2>
                {layerLocationHref}
            </div>
            <TreeVisualizer node={parentNode} tree={tree} />
            <Body2>{translate(Content.map4.layer_information.created_by)}</Body2>
            <Body2>{node.createdByUserName ? <span>{node.createdByUserName}</span> : node.createdByUserId ?? <span>{translate(Content.common.unavailable)}</span>}</Body2>
            <Body2>{translate(Content.map4.layer_information.created_on)}</Body2>
            <Body2>{node.createdAt != null ? format(node.createdAt, "d MMM yy") : <span>{translate(Content.common.unavailable)}</span>}</Body2>
            <Body2>{translate(Content.map4.layer_information.last_modified)}</Body2>
            <Body2>{node.lastUpdatedAt != null ? format(node.lastUpdatedAt, "d MMM yy HH:mm") : <span>{translate(Content.common.unavailable)}</span>}</Body2>
            <Body2>{translate(Content.map4.layer_information.permissions)}</Body2>
            <div>
                <Body2>{node.authorisation}</Body2>
                <PrimaryTextLink onClick={openPermissionsModal}>
                    <Body2>{translate(Content.map4.layer_information.view_collaborators)}</Body2>
                </PrimaryTextLink>
            </div>
        </StyledContainer>
    );
};

const SkeletonPlaceholder = styled(Body2)`
    ${skeletonShimmer({ sequence: 0 })};
    transform: scale(0.9);
`;

const StyledContainer = styled.div`
    display: grid;

    grid-template-columns: auto auto;
    grid-column-gap: ${editStyleFormGap};
    grid-row-gap: ${narrowPadding};
`;

const StyledLink = styled(Link)`
    text-decoration: underline;
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.primary};
`;
